import { isPhysicalPartition, qualified, quoteIdent, tenantPlaceholder } from './target-db.js';

/**
 * SSOT do filtro por varejista em org.extract (port de filter-org-extract-retailers.py).
 * Filtra NA ORIGEM (SELECT parametrizado), não reescreve INSERT linha a linha.
 */

export type RetailerScopeMode = 'include' | 'exclude';

export type TableExtractClass =
  | 'siteGrain'
  | 'configSite'
  | 'bronzeCsv'
  | 'orgWide'
  | 'skipRollup'
  | 'skipOperational';

export interface RetailerScopeInput {
  mode: RetailerScopeMode;
  siteConfigIds: readonly string[];
  /** Aliases CSV resolvidos dos watched sites / retailer_aliases. */
  aliases?: readonly string[];
  /** Host snippets (ex.: mercadolibre.com.ar) para bronze payload. */
  hosts?: readonly string[];
}

export interface ScopedSelect {
  sql: string;
  params: unknown[];
}

export interface TableRef {
  schema: string;
  table: string;
  tenantColumn: 'organization_id' | 'tenant_id';
  dataType: string;
  /** Coluna de site quando conhecida (`site_config_id` | `site_id`). */
  siteColumn?: 'site_config_id' | 'site_id' | null;
}

/** Presets Chamex / Sylvamo — atalho de UI; seleção manual sempre vence. */
export const RETAILER_SPLIT_PRESETS = {
  'chamex-latam': {
    id: 'chamex-latam' as const,
    label: 'Chamex → Sylvamos Latam (include 3)',
    mode: 'include' as const,
    siteConfigIds: [
      '6c401f33-09a9-4fc6-b94c-70c682f4e502', // Mercado Libre AR
      '43efe862-e929-4a98-a460-c3468f0ab728', // Tai Loy PE
      '31330a06-dc77-489f-95e6-648e7bffeced', // CTP Argentina
    ],
    aliases: [
      'Mercado Libre - Argentina',
      'Tai Loy - Peru',
      'Mercado Libre',
      'Tai Loy - PE',
      'CTP Argentina',
    ],
    hosts: ['mercadolibre.com.ar', 'tailoy.com.pe', 'cptoficina.com.ar'],
  },
  'chamex-br-exclude-latam': {
    id: 'chamex-br-exclude-latam' as const,
    label: 'Chamex → Sylvamo BR (exclude LATAM)',
    mode: 'exclude' as const,
    siteConfigIds: [
      '6c401f33-09a9-4fc6-b94c-70c682f4e502', // Mercado Libre AR
      '43efe862-e929-4a98-a460-c3468f0ab728', // Tai Loy PE
    ],
    aliases: [
      'Mercado Libre - Argentina',
      'Tai Loy - Peru',
      'Mercado Libre',
      'Tai Loy - PE',
    ],
    hosts: ['mercadolibre.com.ar', 'tailoy.com.pe'],
  },
} as const;

export type RetailerSplitPresetId = keyof typeof RETAILER_SPLIT_PRESETS;

export const RETAILER_SPLIT_PRESET_IDS = Object.keys(
  RETAILER_SPLIT_PRESETS,
) as RetailerSplitPresetId[];

export const CONFIG_SITE_TABLES = new Set([
  'retailer_aliases',
  'organization_watched_sites',
  'product_site_rules',
  'product_providers',
]);

export const BRONZE_CSV_TABLES = new Set([
  'csv_varejo_dia',
  'csv_produto_site_dia',
  'csv_regra_varejista',
  'csv_sov_dia',
  /** Fato Industry Amazon (e similares) — wipe por varejo_alias + organization_id. */
  'connector_produto_site_dia',
]);

/** Rollups org-wide — sem grão de site; omitir e materializar depois. */
export const SKIP_ROLLUP_TABLES = new Set([
  'industry_summary_mtd',
  'industry_product_d0',
  'industry_product_d7',
  'industry_product_d30',
  'industry_product_d90',
]);

export const SKIP_ROLLUP_PREFIXES = ['industry_category_', 'industry_summary_'] as const;

export const SKIP_OPERATIONAL_TABLES = new Set([
  'audit_logs',
  'csv_uploads',
  'connections',
  'user_organizations',
  'users',
  'data_assets',
  'data_asset_folders',
  'joompulse_category_catalog',
  'joompulse_subscriptions',
  'joompulse_brand_subscriptions',
  'collection_jobs',
  'events',
  'pipeline_executions',
  'site_registration_requests',
  'data_sources',
  'providers',
  'provider_ingest_runs',
  'report_runs',
  'organization_dashboard_settings',
  'organization_settings',
  'organization_storage_quotas',
  'login_methods',
  'organizations',
]);

/** Partições físicas de rollup (industry_product_d30_p2026_07 etc.). */
const ROLLUP_PARTITION_RE =
  /^(industry_product_d(0|7|30|90)|industry_summary_(d(0|7|30|90)|mtd)|industry_category_)/;

export function classifyTable(schema: string, table: string): TableExtractClass {
  if (isPhysicalPartition(table)) return 'skipRollup';

  if (schema === 'public' && SKIP_OPERATIONAL_TABLES.has(table)) {
    return 'skipOperational';
  }

  if (schema === 'gold') {
    if (SKIP_ROLLUP_TABLES.has(table)) return 'skipRollup';
    if (SKIP_ROLLUP_PREFIXES.some((prefix) => table.startsWith(prefix))) return 'skipRollup';
    if (ROLLUP_PARTITION_RE.test(table) && !table.includes('_site_')) return 'skipRollup';
  }

  if (schema === 'public' && CONFIG_SITE_TABLES.has(table)) {
    return 'configSite';
  }

  if (schema === 'bronze') {
    if (BRONZE_CSV_TABLES.has(table)) return 'bronzeCsv';
    // Demais bronze: no modo include, omitimos (sem chave de site confiável).
    return 'bronzeCsv';
  }

  if (schema === 'gold' || schema === 'datahouse' || schema === 'providers_raw' || schema === 'silver') {
    return 'siteGrain';
  }

  return 'orgWide';
}

export function shouldOmitTable(klass: TableExtractClass, mode: RetailerScopeMode): boolean {
  if (klass === 'skipRollup') return true;
  if (klass === 'skipOperational' && mode === 'include') return true;
  return false;
}

export function resolvePreset(
  presetId: RetailerSplitPresetId | undefined,
): RetailerScopeInput | null {
  if (!presetId) return null;
  const preset = RETAILER_SPLIT_PRESETS[presetId];
  if (!preset) return null;
  return {
    mode: preset.mode,
    siteConfigIds: [...preset.siteConfigIds],
    aliases: [...preset.aliases],
    hosts: [...preset.hosts],
  };
}

/**
 * Monta o SELECT scoped. Retorna `null` quando a tabela deve ser omitida do extract.
 * `$1` = organizationId; `$2` = siteConfigIds (quando aplicável); aliases/hosts nos índices seguintes.
 */
export function buildRetailerScopedSelect(
  entry: TableRef,
  scope: RetailerScopeInput,
  limit: number,
): ScopedSelect | null {
  const klass = classifyTable(entry.schema, entry.table);
  if (shouldOmitTable(klass, scope.mode)) return null;

  const lim = Math.max(1, Math.trunc(limit));
  const target = qualified(entry.schema, entry.table);
  const tenant = `${quoteIdent(entry.tenantColumn)} = ${tenantPlaceholder(entry.dataType, 1)}`;
  const siteIds = [...scope.siteConfigIds];
  const aliases = [...(scope.aliases ?? [])];
  const hosts = [...(scope.hosts ?? [])];

  if (klass === 'orgWide') {
    return {
      sql: `SELECT * FROM ${target} WHERE ${tenant} LIMIT ${lim}`,
      params: [],
    };
  }

  if (klass === 'configSite' || klass === 'siteGrain') {
    const siteCol =
      entry.siteColumn ??
      (entry.table === 'organization_watched_sites' ? 'site_id' : 'site_config_id');

    // Tabelas gold/datahouse sem coluna de site: no include omitimos; no exclude mantemos.
    if (klass === 'siteGrain' && entry.siteColumn === null) {
      if (scope.mode === 'include') return null;
      return {
        sql: `SELECT * FROM ${target} WHERE ${tenant} LIMIT ${lim}`,
        params: [],
      };
    }

    const col = quoteIdent(siteCol);
    const predicate =
      scope.mode === 'include'
        ? `${col} = ANY($2::text[])`
        : `NOT (${col} = ANY($2::text[]))`;

    return {
      sql: `SELECT * FROM ${target} WHERE ${tenant} AND ${predicate} LIMIT ${lim}`,
      params: [siteIds],
    };
  }

  // bronzeCsv
  if (BRONZE_CSV_TABLES.has(entry.table)) {
    return buildBronzeCsvSelect(target, tenant, scope.mode, lim, aliases, hosts);
  }

  // Outras tabelas bronze: include = omitir; exclude = exportar tudo da org
  if (scope.mode === 'include') return null;
  return {
    sql: `SELECT * FROM ${target} WHERE ${tenant} LIMIT ${lim}`,
    params: [],
  };
}

function buildBronzeCsvSelect(
  target: string,
  tenant: string,
  mode: RetailerScopeMode,
  lim: number,
  aliases: string[],
  hosts: string[],
): ScopedSelect {
  // $2 = aliases, $3 = hosts
  const aliasMatch = `varejo_alias = ANY($2::text[])`;
  const hostMatch = `(
    EXISTS (
      SELECT 1 FROM unnest($3::text[]) AS h(host)
      WHERE COALESCE(payload_raw::text, '') ILIKE '%' || h.host || '%'
    )
  )`;
  const keep = `(${aliasMatch} OR ${hostMatch})`;
  const predicate = mode === 'include' ? keep : `NOT ${keep}`;

  return {
    sql: `SELECT * FROM ${target} WHERE ${tenant} AND ${predicate} LIMIT ${lim}`,
    params: [aliases, hosts],
  };
}

/** Contagem scoped — mesma lógica do SELECT, sem LIMIT. */
export function buildRetailerScopedCount(
  entry: TableRef,
  scope: RetailerScopeInput,
): ScopedSelect | null {
  const select = buildRetailerScopedSelect(entry, scope, 1);
  if (!select) return null;
  const withoutLimit = select.sql.replace(/\s+LIMIT\s+\d+\s*$/i, '');
  return {
    sql: `SELECT count(*)::bigint AS total FROM (${withoutLimit}) scoped`,
    params: select.params,
  };
}

export function omitComment(schema: string, table: string, reason: string): string {
  return `-- ${schema}.${table} — omitida (${reason})\n`;
}

export function scopeHeaderNotes(scope: RetailerScopeInput, presetId?: string): string[] {
  return [
    `Filtro de varejistas: mode=${scope.mode}${presetId ? ` preset=${presetId}` : ''}`,
    `site_config_id (${scope.siteConfigIds.length}):`,
    ...scope.siteConfigIds.map((id) => `  ${id}`),
    'Rollups org-wide (industry_product_d*, industry_summary_*, industry_category_*) omitidos — materialize após insert.',
    'Catálogo org-wide (product_mappings, categories, brands) incluído por completo.',
  ];
}
